import type { ReactNode } from 'react';
import AppSidebar from '@/components/layout/AppSidebar';
import AppTopBar from '@/components/layout/AppTopBar';
import MvpHeader from '@/components/layout/MvpHeader';
import PrivacyNoticeBanner from '@/components/privacy/PrivacyNoticeBanner';
import { useSidebar } from '@/context/SidebarContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

export default function AppLayout({ children }: { children: ReactNode }) {
  const { collapsed } = useSidebar();
  const { user, acknowledgePrivacy, isSuperUser } = useAuth();
  // Only show when backend explicitly returns null (feature active).
  // undefined means backend hasn't been migrated yet — don't show.
  const showPrivacyNotice = !!user && user.privacyAcknowledgedAt === null;

  // MVP: sólo el SuperUser conserva la sidebar vertical + topbar. El resto de
  // usuarios navega con el mismo chrome de la home/login (MvpHeader).
  if (!isSuperUser) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <MvpHeader />
        <main className="flex-1">
          <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {showPrivacyNotice && <PrivacyNoticeBanner onAcknowledge={acknowledgePrivacy} />}
            {children}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppSidebar />
      <AppTopBar />
      <main
        className={cn(
          'min-h-screen pt-16 transition-[padding] duration-300',
          collapsed ? 'lg:pl-[72px]' : 'lg:pl-[256px]'
        )}
      >
        <div className="px-4 py-5 sm:px-6 lg:px-8 lg:py-6">
          {showPrivacyNotice && <PrivacyNoticeBanner onAcknowledge={acknowledgePrivacy} />}
          {children}
        </div>
      </main>
    </div>
  );
}
